import type { DrawingDocument, Provenance } from '../model';

// Mirrors contracts/isomer.schema.json (chem-isomer.v1): a stereochemistry assignment over a
// Molecule's graph, plus stereo-dependent derived results. `stereochemistry` reuses
// DrawingDocument['structure']['stereochemistry'] directly -- the schema's tetrahedral/
// doubleBonds shapes are identical to the app's own TetrahedralStereo/DoubleBondStereo types,
// same as ChemMoleculeV1Document reuses DrawingDocument['structure']'s atom/bond/charge types
// (see moleculeProjection.ts).
export interface ChemIsomerV1CipPropertySet {
  readonly id: string;
  readonly kind: 'cip';
  readonly isomerId: string;
  readonly isomerRevision: number;
  readonly atomDescriptors: Readonly<Record<string, 'R' | 'S'>>;
  readonly bondDescriptors: Readonly<Record<string, 'E' | 'Z'>>;
  readonly provenance: Provenance;
}

export interface ChemIsomerV1Document {
  readonly schemaVersion: 'chem-isomer.v1';
  readonly id: string;
  readonly revision: number;
  readonly moleculeId: string;
  readonly moleculeRevision: number;
  readonly stereochemistry: DrawingDocument['structure']['stereochemistry'];
  readonly derivedPropertySets: readonly ChemIsomerV1CipPropertySet[];
}

// Projects a live-drawn document's stereo-dependent fields into chem-isomer.v1 shape, mirroring
// toChemMoleculeV1 (moleculeProjection.ts). This is the real save-path projection -- called (via
// moleculeDrawingProjection.ts's toIsomerCreatePayload) every time a drawing with defined
// stereochemistry is saved, not just by tests. `provenance` is carried over from the document's
// own CIP property set (populated by chemicalPerceptionMutations.ts/drawingDocumentFactory.ts
// whenever CIP descriptors are computed) rather than invented here -- the schema requires it,
// and dropping it here (a bug present until this fix) meant any molecule with at least one
// defined stereocenter or double bond failed chem-isomer.v1 validation on save.
export function toChemIsomerV1(document: DrawingDocument): ChemIsomerV1Document {
  const { structure } = document;
  const cip = document.derivedPropertySets.find((set) => set.kind === 'cip');
  return {
    schemaVersion: 'chem-isomer.v1',
    id: structure.id,
    revision: structure.revision,
    moleculeId: structure.id,
    moleculeRevision: structure.revision,
    stereochemistry: structure.stereochemistry,
    derivedPropertySets: cip
      ? [
          {
            id: cip.id,
            kind: 'cip',
            isomerId: structure.id,
            isomerRevision: structure.revision,
            atomDescriptors: cip.atomDescriptors,
            bondDescriptors: cip.bondDescriptors,
            provenance: cip.provenance
          }
        ]
      : []
  };
}
